import {
  httpsCallable,
  type Functions,
  type HttpsCallableOptions,
} from "firebase/functions";
import type {
  ManageContentAction,
  ManageContentRequestMap,
  ManageContentResponseMap,
} from "@um/contracts";

/** Firebase converts undefined object fields to null; omit absent optional fields before encoding. */
export function omitUndefinedFields<Value>(value: Value): Value {
  if (Array.isArray(value)) return value.map(omitUndefinedFields) as Value;
  if (
    value &&
    typeof value === "object" &&
    (Object.getPrototypeOf(value) === Object.prototype ||
      Object.getPrototypeOf(value) === null)
  ) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => [key, omitUndefinedFields(entry)])
    ) as Value;
  }
  return value;
}

/** Accept the caller's Firebase instance so public, account, and screen identities stay separate. */
export function createContentCall<Action extends ManageContentAction>(
  functions: Functions,
  action: Action,
  options?: HttpsCallableOptions
) {
  const call = httpsCallable<
    { action: Action; request: ManageContentRequestMap[Action] },
    ManageContentResponseMap[Action]
  >(functions, "manageContent", options);
  return (request: ManageContentRequestMap[Action]) =>
    call({ action, request: omitUndefinedFields(request) });
}
